<template>
  <teleport to="body">
    <div class="modal-container" v-if="state.isVisible">
      <div class="modal live-lot-alert-popup" ref="modalBlock">
        <div class="modal-title" ref="modalTitle">
          <div class="title-block">
            <div v-if="state.title" class="title">{{ state.title }}</div>
          </div>
          <div class="modal__close" @click="close"></div>
        </div>
        <div class="modal-content">
          <div class="form-group">
            <label for="count-before">
              {{ trans("live.modals.live_lot_alert.labels.count_before") }}
            </label>
            <select id="count-before" class="form-select-field" v-model="state.settings.count_before">
              <option v-for="count in state.countBeforeOptions" :value="count" :key="count">{{ count }}</option>
            </select>
          </div>

          <div class="buttons">
            <button class="btn btn-blue" @click="save">{{ trans("live.modals.live_lot_alert.save_btn") }}</button>
            <button class="btn btn-blue-outline" @click="close">
              {{ trans("live.modals.live_lot_alert.cancel_btn") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-overlay background-fill" v-show="state.isVisible" @click="close"></div>
  </teleport>
</template>

<script setup lang="ts">
  import { inject, onBeforeUnmount, onMounted, reactive, ref } from "vue";
  import { trans } from "@/utils/trans";
  import { useToast } from "primevue/usetoast";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import apiClient from "@/services/api";
  import { ILiveUserAlertSetting } from "@/models";

  const emitter = inject("emitter");
  const toast = useToast();
  const auctionStore = useAuctionStore();

  const state = reactive({
    isVisible: false,
    title: null,
    lotId: null as number,
    settings: null as ILiveUserAlertSetting,
    countBeforeOptions: [...new Array(100).keys()].map((item, i) => i + 1)
  });

  const modalBlock = ref<HTMLDivElement>();

  onMounted(() => {
    document.addEventListener("keydown", closeByEscapeKey);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", closeByEscapeKey);
  });

  const open = () => {
    state.isVisible = true;
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", closeByEscapeKey);
  };

  const close = () => {
    state.isVisible = false;
    // state.customScroll.style.height = "";
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", closeByEscapeKey);
  };

  const closeByEscapeKey = (event) => {
    if (event.keyCode === 27) {
      close();
    }
  };

  const save = () => {
    if (Notification.permission !== "granted") {
      Notification.requestPermission();
    }

    apiClient
      .post(`/live/lots/${state.lotId}/alert`, state.settings)
      .then((response) => {
        if (response.data.status) {
          auctionStore.$patch((storeState) => {
            let lotIndex = storeState.lots.findIndex((lot) => lot.id === response.data.lot_id);
            if (lotIndex >= 0) {
              storeState.lots[lotIndex].alert_enabled = true;
            }
            lotIndex = storeState.catalog?.searchLots?.findIndex((lot) => lot.id === response.data.lot_id);
            if (lotIndex >= 0) {
              storeState.catalog.searchLots[lotIndex].alert_enabled = true;
            }
          });
        }

        toast.add({
          severity: response.data.status ? "success" : "error",
          summary: trans("live.toast." + (response.data.status ? "success" : "error")),
          detail: response.data.message || trans("live.toast.unknown_error"),
          life: 6000
        });

        response.data.status && close();
      })
      .catch((error) => {
        toast.add({
          severity: "error",
          summary: trans("live.toast.error"),
          detail: error?.response?.data.message || trans("live.toast.unknown_error"),
          life: 6000
        });
      });
  };

  emitter?.on(
    "on:live-lot-alerts:modal-show",
    (data: { title: string; lotId: number; settings: ILiveUserAlertSetting }) => {
      if (data.lotId) {
        state.title = data.title;
        state.lotId = data.lotId;
        state.settings = data.settings || { count_before: 1 };
        open();
      } else {
        close();
        toast.add({
          severity: "error",
          summary: trans("live.toast.error"),
          detail: trans("live.toast.not_found.lot"),
          life: 6000
        });
      }
    }
  );
</script>
